/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * WeightEstimatorService.js
 *
 * Estimating goat weight using a linear regression model
 * Based on research datasets and thesis methodology
 *
 * Pipeline Step 5: Gewichtsbestimmung (Weight Estimation)
 * Input: Real-world measurements in cm
 * Output: Estimated weight in kg
 */

/**
 * Linear regression coefficients (trained on body dimensions)
 */
const WEIGHT_MODEL = {
  coefficients: {
    bodyLength: 0.87616975,
    rumpHeight: 0.89817244,
    bodyHeight: 1.22008886,
  },
  intercept: -128.69474242756365,
  features: ['bodyLength', 'rumpHeight', 'bodyHeight'],
  description: 'Linear regression model (Master Thesis Weights)',
}

/**
 * Service for weight estimation
 */
export class WeightEstimatorService {
  constructor(config = {}) {
    this.config = {
      modelPath: '/ml/model.json',
      ...config,
    }
    this.model = WEIGHT_MODEL
    this.isInitialized = false
  }

  /**
   * Initialize the weight estimator
   */
  async initialize() {
    if (this.isInitialized) {
      return
    }

    console.groupCollapsed('[WeightEstimator] Initialized')
    console.info('Model:', this.model.description)
    console.groupEnd()

    this.isInitialized = true
  }

  /**
   * Estimate goat weight from body measurements
   *
   * @param {Object} measurements - Calibrated measurements from CalibrationService
   * @returns {Object} Prediction result
   */
  async estimateWeight(measurements) {
    if (!this.isInitialized) {
      await this.initialize()
    }

    try {
      // Extract target features
      const bodyLength = measurements.lengthCm
      const rumpHeight = measurements.rumpCm
      const bodyHeight = measurements.bodyCm

      // Validate inputs
      if (!this.validateInputMeasurements(bodyLength, rumpHeight, bodyHeight)) {
        throw new Error('Invalid measurements: non-positive values')
      }

      // Apply prediction formula: w = Σ(feat * coef) + intercept
      const weight = this.linearRegression(bodyLength, rumpHeight, bodyHeight)

      // Calculate confidence score based on physical plausibility
      const accuracy = this.calculateModelAccuracy(weight, measurements)

      return {
        weight: weight,
        accuracy: accuracy,
        inputFeatures: {
          bodyLength,
          rumpHeight,
          bodyHeight,
        },
        metadata: {
          calibrationAccuracy: measurements.accuracy,
          totalAccuracy: accuracy * measurements.accuracy,
          model: this.model.description,
        },
      }
    } catch (error) {
      console.error('[WeightEstimator] Weight estimation failed:', error)
      throw new Error(`Weight estimation failed: ${error.message}`)
    }
  }

  /**
   * Linear regression prediction.
   * Implementation based on thesis weight-prediction.ts linearRegression()
   *
   * @param {number} bodyLength - Body length in cm
   * @param {number} rumpHeight - Rump height in cm
   * @param {number} bodyHeight - Body height in cm
   * @returns {number} Predicted weight in kg
   */
  linearRegression(bodyLength, rumpHeight, bodyHeight) {
    const { coefficients, intercept } = this.model

    // Thesis formula:
    // let features = [bodyLength, rumpHeight, bodyHeight]
    // let coef = [0.87616975, 0.89817244, 1.22008886]
    // let intercept = -128.69474242756365
    // const value = features.map((v, i) => v * coef[i]).reduce((p, c) => p + c, 0)
    // const weight = value + intercept

    const weight =
      coefficients.bodyLength * bodyLength +
      coefficients.rumpHeight * rumpHeight +
      coefficients.bodyHeight * bodyHeight +
      intercept

    // Ensure positive weight (from thesis: return weight, not max)
    return Math.max(0, weight)
  }

  /**
   * Calculate model accuracy
   * Based on prediction reasonableness and input quality
   */
  calculateModelAccuracy(weight, measurements) {
    let accuracy = 1.0

    // Typical goat weight range: 20-100 kg
    if (weight < 10 || weight > 120) {
      accuracy *= 0.6 // Outside normal range
    } else if (weight < 20 || weight > 100) {
      accuracy *= 0.8 // Near edge of range
    }

    // Check measurement reasonableness
    const bodyLength = measurements.lengthCm
    const bodyHeight = measurements.bodyCm

    // Typical goat proportions
    if (bodyLength < 40 || bodyLength > 120) {
      accuracy *= 0.8
    }

    if (bodyHeight < 30 || bodyHeight > 100) {
      accuracy *= 0.8
    }

    return accuracy
  }

  /**
   * Validate input measurements
   */
  validateInputMeasurements(bodyLength, rumpHeight, bodyHeight) {
    return (
      typeof bodyLength === 'number' &&
      bodyLength > 0 &&
      typeof rumpHeight === 'number' &&
      rumpHeight > 0 &&
      typeof bodyHeight === 'number' &&
      bodyHeight > 0 &&
      !isNaN(bodyLength) &&
      !isNaN(rumpHeight) &&
      !isNaN(bodyHeight)
    )
  }

  /**
   * Get model information
   */
  getModelInfo() {
    return {
      isInitialized: this.isInitialized,
      inputFeatures: this.model.features,
      coefficients: this.model.coefficients,
      intercept: this.model.intercept,
      description: this.model.description,
      type: 'Linear Regression',
    }
  }

  /**
   * Clean up resources (no-op for linear regression)
   */
  async dispose() {
    this.isInitialized = false
  }

  /**
   * Warmup (no-op for linear regression)
   */
  async warmup() {
    await this.initialize()
  }
}
